import { type ReactNode, useCallback, useRef } from 'react';
import {
  type ToastOptions as ReactToastifyOptions,
  toast as reactToastifyToast,
} from 'react-toastify';
import {
  type ToastTheme,
  useToastThemeStore,
} from '../../store/toastThemeStore';

interface ToastOptions extends ReactToastifyOptions {
  toastTheme: ToastTheme;
}

/**
 * 토스트 내용을 감싸는 상자의 클래스.
 * react-toastify 11에는 내용을 감싸는 요소가 없어서 넘긴 내용이 아이콘, 닫기 버튼과
 * 나란히 놓이는 가로 배치 대상이 된다. 한 겹 감싸 하나로 묶고, 내용 쪽 스타일도 여기에 건다.
 * 토스트 안쪽에만 걸려야 할 스타일이라 다른 곳에서 같은 이름을 쓰지 않도록 서비스 이름을 앞에 붙인다.
 */
export const TOAST_CONTENT_CLASS = 'uglyus-toast-content';

/**
 * react-toastify를 wrapping한 커스텀 toast 훅
 * theme을 포함한 toast 옵션을 한 번에 전달할 수 있습니다.
 *
 * message는 ReactNode만 받습니다. 넘긴 내용을 div로 한 겹 감싸기 때문에
 * 함수를 넘기면 타입 검사는 통과하고 화면을 그리다 죽습니다. 닫기 버튼을
 * 직접 제어하는 토스트가 필요해지면 감싸는 부분부터 바꿔야 합니다.
 */
export const useToast = () => {
  const isActive = reactToastifyToast.isActive;
  const setToastTheme = useToastThemeStore.use.setToastTheme();
  const lastToastIdRef = useRef<string | number | null>(null);

  const showToast = useCallback(
    (type: 'success' | 'error', message: ReactNode, options: ToastOptions) => {
      const { toastTheme, ...restOptions } = options;

      // 새로운 토스트 ID 생성
      const newToastId = `toast-${Date.now()}`;

      setToastTheme(toastTheme);
      reactToastifyToast[type](
        <div className={TOAST_CONTENT_CLASS}>{message}</div>,
        {
          toastId: newToastId,
          ...restOptions,
        },
      );

      // 새로운 토스트 ID 저장
      lastToastIdRef.current = newToastId;
    },
    [setToastTheme],
  );

  return {
    success: useCallback(
      (message: ReactNode, options: ToastOptions) => {
        // 이전 토스트가 활성화되어 있으면 즉시 dismiss
        if (lastToastIdRef.current && isActive(lastToastIdRef.current)) {
          reactToastifyToast.dismiss(lastToastIdRef.current);
        }

        showToast('success', message, options);
      },
      [showToast, isActive],
    ),
    error: useCallback(
      (message: ReactNode, options: ToastOptions) => {
        // 이전 토스트가 활성화되어 있으면 즉시 dismiss
        if (lastToastIdRef.current && isActive(lastToastIdRef.current)) {
          reactToastifyToast.dismiss(lastToastIdRef.current);
        }

        showToast('error', message, options);
      },
      [showToast, isActive],
    ),
  };
};
